import { useMemo, useEffect, useRef, useState } from 'react';
import type { BankingDetails } from '../../../../graphql/generated/graphql';
import type { EditorProps } from '../types/editor-props';
import { BankingDetailsForm } from '../../../forms/banking-details/BankingDetailsForm';
import { useBankingDetailsFormState } from '../../../forms/banking-details/hooks';
import { InlineEditorActions } from './InlineEditorActions';
import { useTranslation } from '../../../../providers/UIProvider';

const isBankingDetails = (value: unknown): value is BankingDetails =>
  typeof value === 'object' && value !== null;

/**
 * Inline editor for structured BankingDetails values.
 * Renders the form directly in-place (no modal). Intended for contexts where
 * opening a dialog would be disruptive, e.g. the ScanEditor review panel.
 *
 * Strips any `bankingDetails.` prefix from incoming issue paths so the form
 * can map issues to specific fields by short key (e.g. `swift`, `iban`).
 */
export function BankingDetailsInlineEditor({
  value,
  onChange,
  onCommit,
  onCancel,
  errors,
  onRegisterSave,
  onDraftChange
}: EditorProps) {
  const t = useTranslation('modules.modals.editBankingDetails');
  const bankingValue = isBankingDetails(value) ? (value as BankingDetails) : null;
  const [saving, setSaving] = useState(false);

  const state = useBankingDetailsFormState({ open: true, bankingDetails: bankingValue });

  const formErrors = useMemo(
    () =>
      (errors ?? []).map(issue => ({
        ...issue,
        path:
          issue.path && issue.path.startsWith('bankingDetails.')
            ? issue.path.slice('bankingDetails.'.length)
            : (issue.path ?? '')
      })),
    [errors]
  );

  const handleFieldChange: typeof state.setField = (field, value) => {
    state.setField(field, value);
    onDraftChange?.();
  };

  const handleSave = async () => {
    if (!state.validateAtLeastOneField()) return;
    setSaving(true);
    try {
      const next: BankingDetails = {
        bankAccount: state.formData.bankAccount || null,
        bankName: state.formData.bankName || null,
        iban: state.formData.iban || null,
        swift: state.formData.swift || null,
        bankCode: state.formData.bankCode || null,
        varSymb: state.formData.varSymb || null,
        constSymb: state.formData.constSymb || null,
        specSymb: state.formData.specSymb || null
      };
      if (onCommit) {
        await onCommit(next);
      } else {
        onChange(next);
      }
    } catch (error) {
      state.setFormError(error instanceof Error ? error.message : t('errors.saveError'));
      if (onRegisterSave) throw error;
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    state.reset();
    onCancel?.();
  };

  // Register save fn with ScanEditor's global save coordinator when present.
  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;
  useEffect(() => {
    if (!onRegisterSave) return;
    onRegisterSave(() => handleSaveRef.current());
    return () => onRegisterSave(null);
  }, [onRegisterSave]);

  return (
    <div className='flex flex-col gap-sm w-full'>
      <BankingDetailsForm
        value={state.formData}
        onFieldChange={handleFieldChange}
        swiftWarning={state.swiftWarning}
        bankCodeWarning={state.bankCodeWarning}
        ibanWarning={state.ibanWarning}
        hasAnyWarning={state.hasAnyWarning}
        formError={state.formError}
        errors={formErrors}
        disabled={saving}
      />
      {!onRegisterSave && (
        <InlineEditorActions t={t} saving={saving} onCancel={handleCancel} onSave={handleSave} />
      )}
    </div>
  );
}

BankingDetailsInlineEditor.displayName = 'BankingDetailsInlineEditor';
